import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { voiceApi, type VoicemailHeardResponse } from "api/voice";
import { MARK_AS_HEARD_FAILED_TOAST } from "shared/app/Voicemail/voicemail";
import { getErrorMessage } from "utils/error";

/**
 * CRM-1777: Mark as heard — clears a voicemail's New mark for the whole team
 * without playing it, for a team that acted on the transcript.
 */
export const useMarkVoicemailHeard = (
  callId: number | string,
  { onHeard }: { onHeard: (response: VoicemailHeardResponse) => void }
) => {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationKey: ["VOICEMAIL", "HEARD", "UPDATE"],
    mutationFn: (id: number | string) => voiceApi.markVoicemailHeard(id),
    onSuccess: (response) => {
      onHeard(response);
      // The Calls log and its drawer show who heard it.
      queryClient.invalidateQueries({ queryKey: ["CALLS"] });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) || MARK_AS_HEARD_FAILED_TOAST);
    }
  });

  const markHeard = useCallback(() => mutate(callId), [callId, mutate]);

  return { markHeard, isPending };
};
